import { coreCall } from './gatewayService';

/**
 * Evasione degli ordini cliente con i DDT in uscita.
 *
 * Unico punto del frontend che chiede a flusia-core quanto di ogni riga d'ordine
 * è già in un DDT, spedito, consegnato e ancora da evadere. La regola vive in
 * flusia-core `orders/ddtAllocation.ts` e `orders/delivery.ts`: qui non si
 * duplica nulla, si chiama e si traduce la risposta.
 */

export interface OrderDeliveryItem {
  id: string;
  order_id: string;
  product_id: string;
  product_name: string;
  product_sku: string | null;
  unit_of_measure: string | null;
  unit_price: number;
  /** Quantità ordinata */
  quantity: number;
  /** In qualsiasi DDT, bozze comprese */
  in_ddt: number;
  /** In DDT spediti o consegnati */
  shipped: number;
  /** In DDT consegnati */
  delivered: number;
  /** Ancora da mettere in un DDT */
  remaining: number;
}

export interface OrderDeliveryDdt {
  id: string;
  ddt_number: string | null;
  ddt_date: string | null;
  status: string;
}

export interface OrderDelivery {
  id: string;
  order_number: string;
  status: string;
  order_date: string | null;
  shipping_location_id: string | null;
  shipping_address: Record<string, string | undefined> | null;
  items: OrderDeliveryItem[];
  ddts: OrderDeliveryDdt[];
  has_remaining: boolean;
}

/**
 * Ordini con righe evase dai DDT.
 * - `orderIds`: gli ordini indicati (dettaglio ordine)
 * - `clientId`: ordini del cliente ancora da evadere (modale «Da ordine»)
 * - `excludeDdtId`: il DDT in modifica non conta
 */
export async function loadOrdersDelivery(params: {
  organizationId: string;
  orderIds?: string[];
  clientId?: string;
  excludeDdtId?: string | null;
}): Promise<OrderDelivery[]> {
  const { data, error } = await coreCall('orders/delivery-lines', {
    body: {
      organization_id: params.organizationId,
      ...(params.orderIds ? { order_ids: params.orderIds } : { client_id: params.clientId }),
      ...(params.excludeDdtId ? { exclude_ddt_id: params.excludeDdtId } : {}),
    },
  });
  if (error) throw new Error(error.message);
  return (data?.orders ?? []) as OrderDelivery[];
}

/**
 * Dopo un cambio di stato di un DDT in uscita: aggiorna lo stato degli ordini
 * collegati. flusia-core non fa nulla se l'organizzazione non ha attivato
 * l'impostazione. Best-effort: un errore non blocca il DDT.
 */
export async function syncOrdersFromOutboundDdt(organizationId: string, ddtId: string): Promise<void> {
  try {
    const { error } = await coreCall('orders/sync-shipping-from-ddt', {
      body: { organization_id: organizationId, ddt_id: ddtId },
    });
    if (error) console.warn('Aggiornamento stato ordini dal DDT non riuscito:', error.message);
  } catch (err) {
    console.warn('Aggiornamento stato ordini dal DDT non riuscito:', err);
  }
}
